import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { LogOut, Package, ArrowLeft } from 'lucide-react';
import { useAuth, api } from '../App';
import { ASSETS } from '../constants/site';

export default function Account() {
  const location = useLocation();
  const { user, loading, login, logout } = useAuth();
  const [payments, setPayments] = useState([]);
  const effectiveUser = user || location.state?.user;

  useEffect(() => {
    if (effectiveUser) {
      api.get('/payments/my').then((r) => setPayments(r.data)).catch(() => {});
    }
  }, [effectiveUser]);

  if (loading && !effectiveUser) {
    return (
      <div className="min-h-screen bg-[#050B14] flex items-center justify-center">
        <div className="font-mono-tech text-amber-400/80 text-sm tracking-widest animate-pulse">VERIFYING SESSION…</div>
      </div>
    );
  }

  if (!effectiveUser) {
    return (
      <div className="min-h-screen bg-[#050B14] flex items-center justify-center px-6">
        <div className="gold-ring rounded-3xl bg-[#0F1C33]/70 p-10 max-w-md w-full text-center">
          <img src={ASSETS.icon} alt="GSLAB" className="h-16 w-16 rounded-xl mx-auto mb-6" />
          <h1 className="font-display font-bold text-2xl mb-3">Client Portal</h1>
          <p className="text-sm text-slate-400 mb-8">Sign in with your Google account to view your GSLAB projects and active plans.</p>
          <button
            data-testid="account-login-button"
            onClick={login}
            className="w-full bg-gradient-to-r from-[#D4AF37] via-[#F3C64F] to-[#D4AF37] text-[#050B14] font-bold rounded-full px-6 py-3.5"
          >
            Sign in with Google
          </button>
          <a href="/" className="inline-flex items-center gap-2 text-xs text-slate-500 hover:text-amber-300 mt-6 transition-colors">
            <ArrowLeft size={13} /> Back to gslab.engineer
          </a>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="account-dashboard" className="min-h-screen bg-[#050B14] pt-28 pb-20 px-4 sm:px-8">
      <div className="max-w-4xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="flex items-center justify-between mb-10">
          <div className="flex items-center gap-4">
            {effectiveUser.picture ? (
              <img src={effectiveUser.picture} alt="" className="h-14 w-14 rounded-full border border-amber-500/40" />
            ) : (
              <span className="h-14 w-14 rounded-full bg-amber-500/20 border border-amber-500/40 flex items-center justify-center text-amber-300 font-bold text-xl">
                {effectiveUser.name?.[0] || 'U'}
              </span>
            )}
            <div>
              <h1 className="font-display font-bold text-2xl">{effectiveUser.name}</h1>
              <p className="text-sm text-slate-500">{effectiveUser.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" className="text-sm text-slate-400 hover:text-amber-300 transition-colors">← Site</a>
            <button data-testid="account-logout-button" onClick={logout} className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-red-400 border border-slate-700 rounded-full px-4 py-2 transition-colors">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </motion.div>

        <h2 className="font-mono-tech text-xs tracking-[0.3em] text-amber-400/90 uppercase mb-5 flex items-center gap-2">
          <Package size={14} /> Your Plans & Purchases
        </h2>
        {payments.length === 0 ? (
          <div className="border border-slate-800 rounded-2xl bg-[#0F1C33]/60 p-10 text-center">
            <p className="text-slate-400 text-sm mb-6">No active plans yet. Pick a build or care plan to get your engine started.</p>
            <a href="/#pricing" data-testid="account-view-pricing-link" className="inline-block bg-gradient-to-r from-[#D4AF37] to-[#F3C64F] text-[#050B14] font-bold rounded-full px-7 py-3 text-sm">
              View Pricing
            </a>
          </div>
        ) : (
          <div className="space-y-3">
            {payments.map((p) => (
              <div key={p.session_id} className="flex items-center justify-between border border-slate-800 rounded-2xl bg-[#0F1C33]/60 px-6 py-5">
                <div>
                  <div className="font-display font-semibold">{p.package_name}</div>
                  <div className="font-mono-tech text-[10px] tracking-widest text-slate-500 mt-1 uppercase">
                    {p.kind === 'subscription' ? 'MONTHLY PLAN' : 'ONE-TIME'} · ${Number(p.amount).toLocaleString()}
                  </div>
                </div>
                <span className="font-mono-tech text-[10px] tracking-widest text-emerald-300 border border-emerald-500/40 rounded-full px-4 py-1.5">ACTIVE</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
